import axios from 'axios';
import type { DestinationHealthInfo } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Type alias for consistency with the user's request
export type DestinationInfoResponse = DestinationHealthInfo;

// Destination list item from API
interface DestinationListItem {
  id: string; // ISO2 code
  country_id: number;
  name: string;
  region: string;
  subregion: string;
  cdc_advisory_level: number;
  vaccine_count: number;
  active_notices: number;
  has_malaria_risk: boolean;
  has_altitude_risk: boolean;
  has_diarrhea_risk: boolean;
  starting_price: number;
  pricing_note: string;
}

// Create a separate axios instance for public endpoints (no auth required)
const publicApi = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add logging interceptors
publicApi.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

publicApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

import { apiCache, cacheKeys, cacheTTL } from '@/utils/api-cache';

/**
 * Fetch all destinations from API with unified caching
 */
const fetchDestinations = async (): Promise<DestinationListItem[]> => {
  return apiCache.fetch(
    cacheKeys.destinations(),
    async () => {
      try {
        const response = await publicApi.get<{ destinations: DestinationListItem[]; total: number }>('/destinations');
        return response.data.destinations;
      } catch (error) {
        console.error('Failed to fetch destinations list:', error);
        // Return empty array if fetch fails
        return [];
      }
    },
    cacheTTL.veryLong // 1 hour - static data
  );
};

/**
 * Convert country name to ISO2 code using API data
 */
const getISO2Code = async (countryName: string): Promise<string | null> => {
  const cleanedName = countryName.trim();
  const destinations = await fetchDestinations();
  
  // Find matching destination by name (case-insensitive)
  const destination = destinations.find(
    (d) => d.name.toLowerCase() === cleanedName.toLowerCase()
  );
  
  return destination ? destination.id : null;
};

/**
 * Get destination info by ISO2 code with caching
 * This is a public endpoint that doesn't require authentication
 *
 * @param iso2Code - ISO2 country code (e.g., 'US', 'CZ')
 * @returns Promise with destination health information
 */
export const getDestinationByCode = async (iso2Code: string): Promise<DestinationInfoResponse> => {
  const normalizedCode = iso2Code.toLowerCase();
  const cacheKey = cacheKeys.destinationByCode(normalizedCode);
  
  return apiCache.fetch(
    cacheKey,
    async () => {
      try {
        const response = await publicApi.get<DestinationInfoResponse>(
          `/destinations/${normalizedCode}/info`
        );
        return response.data;
      } catch (error: any) {
        console.error(`Failed to fetch destination info for ${iso2Code}:`, error);
        throw new Error(
          error.response?.data?.error ||
          `Failed to fetch destination information for ${iso2Code}`
        );
      }
    },
    cacheTTL.veryLong // 1 hour - static data
  );
};

/**
 * Get destination info by country name from destination object
 * Converts country name to ISO2 code and uses the ISO2 endpoint
 * Results are cached by country name
 */
export const getDestinationInfoByCountry = async (
  countryName: string,
  travelerCount: number = 1
): Promise<DestinationHealthInfo> => {
  // Clean the country name (remove extra spaces, normalize)
  const cleanedName = countryName.trim();
  const cacheKey = cacheKeys.destinationInfo(cleanedName);

  return apiCache.fetch(
    cacheKey,
    async () => {
      // Try to get ISO2 code from API
      const iso2Code = await getISO2Code(cleanedName);

      if (iso2Code) {
        // Use the ISO2 endpoint (which is also cached)
        return getDestinationByCode(iso2Code);
      } else {
        // No ISO2 code found - country not in destinations list
        throw new Error(`Destination "${cleanedName}" not found in available destinations`);
      }
    },
    cacheTTL.veryLong // 1 hour - static data
  );
};

export default {
  getDestinationInfoByCountry,
  getDestinationByCode,
};
